import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';

/** @type {import('../../../contracts/forms.js').FieldDriver} */
export const checkboxGroupDriver = {
  type: 'checkbox-group',
  mount(container, ctx) {
    const { def, onChange } = ctx;
    const cbgDef = /** @type {import('../../../contracts/forms.js').CheckboxGroupFieldDef} */ (def);

    const group = createElement('div', {
      class: 'checkbox-group',
      id: `field-${def.key}`,
    });
    if (def.required) group.setAttribute('aria-required', 'true');
    container.appendChild(group);

    if (cbgDef.loadOptions) {
      // --- Async path ---
      let destroyed = false;
      /**
       * Stable reference — only replaced when contents actually change.
       * getValue() returns this directly so Object.is() detects real changes.
       * @type {string[]}
       */
      let currentValues = Array.isArray(ctx.value) ? [.../** @type {string[]} */ (ctx.value)] : [];
      /** @type {ReadonlyArray<import('../../../contracts/forms.js').FieldOption> | null} */
      let prevOptions = null;
      /** @type {HTMLInputElement[]} */
      let checkboxes = [];
      let forcedDisabled = false;

      const provider = createOptionsProvider({
        options: cbgDef.options,
        loadOptions: cbgDef.loadOptions,
        mapOption: cbgDef.mapOption,
      });

      /**
       * @param {import('../../../contracts/forms.js').FieldOption[]} options
       */
      function renderCheckboxes(options) {
        group.textContent = '';
        checkboxes = [];
        for (const opt of options) {
          const cbId = `field-${def.key}-${opt.value}`;
          const cbLabel = createElement('label', { class: 'checkbox-label', for: cbId });
          const cb = /** @type {HTMLInputElement} */ (createElement('input', {
            type: 'checkbox',
            id: cbId,
            name: def.key,
            value: opt.value,
          }));
          if (currentValues.includes(opt.value)) cb.checked = true;
          if (forcedDisabled || Boolean(def.disabled)) cb.disabled = true;
          cb.addEventListener('change', () => {
            // User-initiated: always allocate new array (signals real change to Object.is)
            currentValues = checkboxes.filter(c => c.checked).map(c => c.value);
            onChange(currentValues);
          });
          checkboxes.push(cb);
          cbLabel.appendChild(cb);
          cbLabel.appendChild(document.createTextNode(` ${opt.label}`));
          group.appendChild(cbLabel);
        }
      }

      /**
       * @param {import('../../../contracts/forms.js').FieldOption[]} options
       */
      function rebuildCheckboxes(options) {
        renderCheckboxes(options);
        const available = new Set(options.map(o => o.value));
        const filtered = currentValues.filter(v => available.has(v));
        if (filtered.length !== currentValues.length) {
          currentValues = filtered;
          onChange(currentValues);
        }
      }

      function applyDisabled() {
        for (const cb of checkboxes) {
          cb.disabled = forcedDisabled || Boolean(def.disabled);
        }
      }

      const unsubscribe = subscribeWithInitial(provider, (state) => {
        if (destroyed) return;
        group.setAttribute('aria-busy', state.loading ? 'true' : 'false');
        if (state.options !== prevOptions) {
          if (prevOptions === null) {
            prevOptions = state.options;
            renderCheckboxes(state.options);
          } else {
            prevOptions = state.options;
            rebuildCheckboxes(state.options);
          }
        }
      });

      provider.load('');

      return {
        setValue(value) {
          // Host-driven: allocate new array (contents changed)
          currentValues = Array.isArray(value) ? [.../** @type {string[]} */ (value)] : [];
          for (const cb of checkboxes) {
            cb.checked = currentValues.includes(cb.value);
          }
        },
        getValue() {
          // Return stable reference so Object.is(prev, next) is true when unchanged
          return currentValues;
        },
        setErrors() {
          // errors rendered by host
        },
        destroy() {
          destroyed = true;
          unsubscribe();
          provider.destroy();
          group.remove();
        },
        setDisabled(disabled) {
          forcedDisabled = disabled;
          applyDisabled();
        },
        clearValue() {
          // Idempotent: no-op if already empty — keeps same [] reference
          if (currentValues.length === 0) return;
          currentValues = [];
          for (const cb of checkboxes) {
            cb.checked = false;
          }
        },
        getOptionsProvider() {
          return provider;
        },
        setAriaInvalid(invalid) {
          group.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        },
      };
    }

    // --- Static path ---
    const initialSelected = Array.isArray(ctx.value) ? /** @type {string[]} */ (ctx.value) : [];
    /**
     * Stable reference — only replaced when contents actually change.
     * @type {string[]}
     */
    let currentValues = [...initialSelected];
    /** @type {HTMLInputElement[]} */
    const checkboxes = [];
    let forcedDisabled = false;

    for (const opt of cbgDef.options ?? []) {
      const cbId = `field-${def.key}-${opt.value}`;
      const cbLabel = createElement('label', { class: 'checkbox-label', for: cbId });
      const cb = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'checkbox',
        id: cbId,
        name: def.key,
        value: opt.value,
      }));
      if (initialSelected.includes(opt.value)) cb.checked = true;
      if (def.disabled) cb.disabled = true;
      cb.addEventListener('change', () => {
        // User-initiated: always allocate new array (signals real change to Object.is)
        currentValues = checkboxes.filter(c => c.checked).map(c => c.value);
        onChange(currentValues);
      });
      checkboxes.push(cb);
      cbLabel.appendChild(cb);
      cbLabel.appendChild(document.createTextNode(` ${opt.label}`));
      group.appendChild(cbLabel);
    }

    return {
      setValue(value) {
        // Host-driven: allocate new array (contents changed)
        currentValues = Array.isArray(value) ? [.../** @type {string[]} */ (value)] : [];
        for (const cb of checkboxes) {
          cb.checked = currentValues.includes(cb.value);
        }
      },
      getValue() {
        // Return stable reference so Object.is(prev, next) is true when unchanged
        return currentValues;
      },
      setErrors() {
        // errors rendered by host
      },
      destroy() {
        group.remove();
      },
      setDisabled(disabled) {
        forcedDisabled = disabled;
        for (const cb of checkboxes) {
          cb.disabled = forcedDisabled || Boolean(def.disabled);
        }
      },
      clearValue() {
        // Idempotent: no-op if already empty — keeps same [] reference
        if (currentValues.length === 0) return;
        currentValues = [];
        for (const cb of checkboxes) {
          cb.checked = false;
        }
      },
      setAriaInvalid(invalid) {
        group.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      },
    };
  },
};
